import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
//mui
import { Card, TextField, Typography, Container, Box, Autocomplete } from '@mui/material';
import { makeStyles } from '@mui/styles';
import ContactMailOutlinedIcon from '@mui/icons-material/ContactMailOutlined';
//components
import { HBox } from '../../../../assets/SharedComponents.jsx';

const useStyles = makeStyles(() => ({
    foodCard: {
        maxHeight: '100% !important',
        borderRadius: '1.7rem !important',
        margin: '0.8rem !important',
    },
    cardTitleAndIcon: {
        marginRight: '0.5rem !important',
        marginTop: '0.3rem !important',
        alignItems: 'center !important',
    },
    cardTitle: {
        margin: '0.5rem !important',
        fontSize: '0.9rem !important',
        alignItems: 'center !important',
        fontWeight: "bold !important"
    },
    statmentAutoComplite: {
        '&.MuiAutocomplete-root .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
            width: "0 !important",
            minWidth: "0px !important"
        }
    },
    RemoveTextFieldNumberArrow: {
        '& .MuiOutlinedInput-input': {
            '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': {
                '-webkit-appearance': 'none',
            }
        }
    },
    tc: {
        "&.MuiTableCell-body": {
            padding: '0 !important'
        }
    }
}));

const AddChildDynamicField = ({ household, cardCode, childClass, childBirthData, childPhoneData, childShirtSize, childEmigrationYear, setDynamicValidator, clearSignature, countriesList = [] }) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const inputRef1 = useRef();
    const [childPhone, setChildPhone] = useState("");
    const [phoneValid, setPhoneValid] = useState(false);
    const [birthCountry, setBirthCountry] = useState(childBirthData || "");
    const [displayCountry, setDisplayCountry] = useState(false);
    const [displayPhone, setDisplayPhone] = useState(false);
    const [displayShirtSize, setDisplayShirtSize] = useState(false);
    const [emigrationYear, setEmigrationYear] = useState(childEmigrationYear || "");
    const needsEmigrationYear = !!birthCountry && birthCountry !== "IL";
    // only prompt when the year isn't already on file - already-set values shouldn't be re-requested every statement
    const displayEmigrationYear = needsEmigrationYear && !childEmigrationYear;

    const shirtSizesList = [
        { size: '10' },
        { size: '12' },
        { size: '14' },
        { size: '16' },
        { size: 'XS' },
        { size: 'S' },
        { size: 'M' },
        { size: 'L' },
        { size: 'XL' },
        { size: 'XXL' },
        { size: 'XXXL' }
    ];

    // Initialize shirtSize with the matching object if childShirtSize exists
    const [shirtSize, setShirtSize] = useState(
        childShirtSize ? shirtSizesList.find(s => s.size === childShirtSize) || "" : ""
    );

    //initialize phone and birthState
    useEffect(() => {
        if (!childBirthData) {
            setDisplayCountry(true);
        }
        if (!childPhoneData && ["8", "9", "10"].includes(childClass)) {
            setDisplayPhone(true);
        }

        setDisplayShirtSize(true);

    }, [childPhoneData, childBirthData, childShirtSize]);

    // insert to household for every change
    useEffect(() => {
        clearSignature();

        // Check each required field separately and combine with AND logic
        let isValid = true;

        if (displayCountry) {
            isValid = isValid && birthCountry.length > 0;
        }
        if (displayPhone) {
            isValid = isValid && childPhone.length > 0 && phoneValid;
        }
        if (displayShirtSize) {
            isValid = isValid && shirtSize?.size && shirtSize.size.length > 0;
        }
        if (displayEmigrationYear) {
            isValid = isValid && /^\d{4}$/.test(emigrationYear);
        }

        setDynamicValidator(isValid);

        if (isValid) {
            insertToHousehold();
        }
    }, [childPhone, birthCountry, shirtSize, emigrationYear, displayCountry, displayPhone, displayShirtSize, displayEmigrationYear]);

    //code list creation to update child food prefer
    const insertToHousehold = () => {
        const child = household.ChildrenList?.results.find((child) => child.CardCode === cardCode);
        child.PlaceOfBirth = birthCountry;
        child.Phone2 = childPhone;
        child.Shirts = shirtSize?.size;
        child.Pupil_EmigrationDate = needsEmigrationYear ? emigrationYear : '';
    }

    const handleEmigrationYearChange = (e) => {
        const numericValue = e.target.value.replace(/\D/g, '').slice(0, 4);
        setEmigrationYear(numericValue);
    };

    const handlePhoneChange = (e) => {
        const value = e.target.value.trim();
        const numericValue = value.replace(/\D/g, '');

        setChildPhone(numericValue);

        const isValid = /^05\d{8}$/.test(numericValue);
        setPhoneValid(isValid);
    };

    const countryValueChanged = (value) => {
        setBirthCountry(value.CountryCode);
    }
    const sizeValueChanged = (value) => {
        setShirtSize(value);
    }

    return (
        <>
            <Card className={classes.foodCard} data-html2canvas-ignore="true">
                <Container>
                    <HBox className={classes.cardTitleAndIcon}>
                        <ContactMailOutlinedIcon sx={{ color: "#3a7735" }} />
                        <Typography className={classes.cardTitle}>{t('addMissingData')} </Typography>
                    </HBox>
                    {displayCountry && <Box flex={1} display='flex' flexDirection='column' alignItems="flex-start" padding='1rem'>
                        <Typography variant='smallText'>{t('birthCountry')} </Typography>
                        <Autocomplete
                            options={
                                countriesList?.sort((a, b) =>
                                    a.CountryName.toString().localeCompare(b.CountryName.toString())
                                )}
                            renderOption={(props, option) => (
                                <Box component="li" {...props} key={option.CountryCode}>
                                    {option.CountryName}
                                </Box>
                            )}
                            fullWidth
                            isOptionEqualToValue={(option, value) => option.CountryCode === value.CountryCode}
                            onChange={(e, value) => countryValueChanged(value)}
                            getOptionLabel={(option) => `${option.CountryName}`}
                            className={classes.statmentAutoComplite}
                            noOptionsText={t('countryNoOption')}
                            sx={{ mr: '0.9rem' }}
                            renderInput={(params) => (
                                <TextField {...params} variant='standard' fullWidth />
                            )}
                        />
                    </Box>}
                    {displayEmigrationYear && <Box flex={1} display='flex' flexDirection='column' alignItems="flex-start" padding='1rem'>
                        <Typography variant='smallText'>{t('pupilEmigrationYear')} </Typography>
                        <TextField
                            fullWidth
                            className={classes.RemoveTextFieldNumberArrow}
                            type="text"
                            value={emigrationYear}
                            onChange={handleEmigrationYearChange}
                            helperText={emigrationYear !== '' && !/^\d{4}$/.test(emigrationYear) ? t('insertValidYear') : ' '}
                            error={emigrationYear !== '' && !/^\d{4}$/.test(emigrationYear)}
                            variant="standard"
                            inputProps={{
                                inputMode: 'numeric',
                                pattern: '[0-9]*',
                                maxLength: 4,
                            }}
                        />
                    </Box>}
                    {displayShirtSize && <Box flex={1} display='flex' flexDirection='column' alignItems="flex-start" padding='1rem'>
                        <Typography variant='smallText'>{t('shirtSize')} </Typography>
                        <Autocomplete
                            options={shirtSizesList}
                            renderOption={(props, option) => (
                                <Box component="li" {...props} key={option.size}>
                                    {option.size}
                                </Box>
                            )}
                            fullWidth
                            defaultValue={shirtSize}
                            isOptionEqualToValue={(option, value) => option.size === value.size}
                            onChange={(e, value) => sizeValueChanged(value)}
                            getOptionLabel={(option) => option?.size || ''}
                            className={classes.statmentAutoComplite}
                            noOptionsText={t('noOption')}
                            sx={{ mr: '0.9rem' }}
                            renderInput={(params) => (
                                <TextField {...params} variant='standard' fullWidth />
                            )}
                        />
                    </Box>}
                    {displayPhone && (
                        <Box flex={1} display="flex" flexDirection="column" alignItems="flex-start" p={2}>
                            <Typography variant="smallText">{t('myPhone')}</Typography>
                            <TextField
                                fullWidth
                                className={`${classes.RemoveTextFieldNumberArrow} ${classes.statmentAutoComplite}`}
                                type="tel"
                                value={childPhone}
                                onChange={handlePhoneChange}
                                helperText={!phoneValid && childPhone !== '' ? t('insertValidPhone') : ' '}
                                error={!phoneValid && childPhone !== ''}
                                variant="standard"
                                inputProps={{
                                    inputMode: 'tel',
                                    pattern: '[0-9]*',
                                    maxLength: 10,
                                }}
                                InputProps={{
                                    inputRef: inputRef1,
                                }}
                                onWheel={() => {
                                    if (inputRef1?.current?.blur) {
                                        inputRef1.current.blur();
                                    }
                                }}
                            />
                        </Box>
                    )}
                </Container>
            </Card>
        </>
    )
}
export default AddChildDynamicField;